import React, { useState, useEffect } from "react";
import { LegalLayout, P, Strong, UL, LI, Callout } from "./LegalLayout";

export default function TermsOfUse() {
  const [lang, setLang] = useState(() => localStorage.getItem("nexum-lang") || "es");
  useEffect(() => {
    localStorage.setItem("nexum-lang", lang);
    window.scrollTo(0, 0);
  }, [lang]);

  const content = {
    es: {
      title: "Términos de Uso",
      subtitle:
        "Estas condiciones regulan el acceso y uso del sitio web y de los servicios de Nexum. Léelas con detenimiento; al utilizar el servicio, aceptas quedar vinculado por ellas.",
      lastUpdated: "Marzo 2026",
      sections: [
        {
          id: "objeto",
          heading: "Objeto",
          body: (
            <>
              <P>
                Los presentes Términos de Uso regulan el acceso, la navegación y la utilización de este sitio web ({" "}
                <Strong>nexumbusiness.com</Strong>) y de los servicios digitales prestados por <Strong>Nexum Business S.L.</Strong> (en adelante, <Strong>&ldquo;Nexum&rdquo;</Strong>, <Strong>&ldquo;nosotros&rdquo;</Strong> o <Strong>&ldquo;el Prestador&rdquo;</Strong>).
              </P>
              <P>
                El uso del sitio web atribuye la condición de Usuario y supone la aceptación plena y sin reservas de todas las cláusulas aquí recogidas.
              </P>
            </>
          ),
        },
        {
          id: "prestador",
          heading: "Identificación del prestador",
          body: (
            <UL>
              <LI><Strong>Denominación social:</Strong> Nexum Business S.L.</LI>
              <LI><Strong>Domicilio social:</Strong> Barcelona, España</LI>
              <LI><Strong>Correo electrónico:</Strong> hello@nexumbusiness.com</LI>
              <LI><Strong>Actividad:</Strong> desarrollo y explotación de soluciones de inteligencia artificial conversacional para el sector de la automoción.</LI>
            </UL>
          ),
        },
        {
          id: "acceso",
          heading: "Acceso y uso del sitio",
          body: (
            <>
              <P>
                El acceso al sitio es <Strong>gratuito</Strong>, salvo en lo relativo al coste de la conexión a Internet contratado por el Usuario. Algunos servicios pueden requerir registro previo y contratación específica.
              </P>
              <P>El Usuario se compromete a utilizar el sitio conforme a la Ley, a la moral, al orden público y a estos Términos. En particular, se obliga a:</P>
              <UL>
                <LI>No realizar actos contrarios a los derechos de terceros o al buen funcionamiento del servicio.</LI>
                <LI>No introducir ni difundir programas, virus, macros, applets, controles ActiveX o cualquier código lesivo.</LI>
                <LI>No manipular ni intentar acceder a áreas restringidas del sistema.</LI>
                <LI>No usar el servicio para envío masivo no autorizado de comunicaciones (spam).</LI>
                <LI>No realizar ingeniería inversa, decompilación ni descompilación del software.</LI>
              </UL>
            </>
          ),
        },
        {
          id: "propiedad",
          heading: "Propiedad intelectual e industrial",
          body: (
            <>
              <P>
                Todos los contenidos del sitio, incluyendo con carácter enunciativo pero no limitativo textos, fotografías, gráficos, imágenes, iconos, tecnología, software, código fuente, diseño gráfico, nombres comerciales, marcas o signos distintivos, son <Strong>propiedad de Nexum</Strong> o de terceros que han autorizado su uso.
              </P>
              <P>
                Queda expresamente prohibida su reproducción, distribución, comunicación pública, transformación o cualquier otra actividad que se realice con los contenidos, ni siquiera citando las fuentes, salvo consentimiento por escrito de Nexum.
              </P>
              <Callout>
                Cualquier uso no autorizado será considerado un incumplimiento grave y podrá dar lugar a las acciones legales que correspondan.
              </Callout>
            </>
          ),
        },
        {
          id: "enlaces",
          heading: "Enlaces (links)",
          body: (
            <>
              <P>
                El sitio puede contener enlaces a sitios web de terceros. Nexum <Strong>no controla ni asume responsabilidad</Strong> sobre los contenidos, políticas de privacidad o prácticas de dichos sitios.
              </P>
              <P>
                Los enlaces se ofrecen exclusivamente con finalidad informativa y su inclusión no implica ninguna asociación, fusión ni participación con las entidades vinculadas.
              </P>
            </>
          ),
        },
        {
          id: "responsabilidad",
          heading: "Exclusión de garantías y responsabilidad",
          body: (
            <>
              <P>
                Nexum realiza sus mejores esfuerzos para mantener el sitio y el servicio operativos, seguros y libres de errores. No obstante, no puede garantizar la <Strong>ausencia absoluta</Strong> de interrupciones o errores en el acceso, ni la ausencia de virus u otros elementos lesivos, siempre que hayan sido introducidos por terceros ajenos a Nexum.
              </P>
              <P>
                Nexum queda exonerada de cualquier responsabilidad derivada de:
              </P>
              <UL>
                <LI>La falta de disponibilidad puntual del sitio por causas de fuerza mayor o mantenimiento.</LI>
                <LI>La utilización indebida del sitio por parte de los Usuarios.</LI>
                <LI>Los contenidos y opiniones vertidos por terceros a través de canales del sitio.</LI>
                <LI>Los daños o perjuicios derivados del uso o interpretación de la información publicada.</LI>
              </UL>
            </>
          ),
        },
        {
          id: "modificaciones",
          heading: "Modificaciones",
          body: (
            <P>
              Nexum se reserva el derecho a efectuar en cualquier momento y sin previo aviso <Strong>modificaciones</Strong> en la información contenida en el sitio, en la configuración y presentación, o en las condiciones de acceso. Recomendamos revisar periódicamente este documento.
            </P>
          ),
        },
        {
          id: "duracion",
          heading: "Duración y terminación",
          body: (
            <P>
              La prestación del servicio del sitio y de los distintos servicios tiene, en principio, una <Strong>duración indefinida</Strong>. Nexum podrá dar por terminada, suspender o interrumpir unilateralmente y sin necesidad de preaviso la prestación del servicio y del sitio web, sin perjuicio de lo que se hubiere dispuesto en las condiciones contractuales particulares.
            </P>
          ),
        },
        {
          id: "legislacion",
          heading: "Legislación aplicable y jurisdicción",
          body: (
            <>
              <P>
                La relación entre Nexum y el Usuario se regirá por la <Strong>legislación española vigente</Strong>. Cualquier controversia se someterá a los Juzgados y Tribunales de la ciudad de <Strong>Barcelona</Strong>, con renuncia expresa a cualquier otro fuero que pudiera corresponder, salvo que la ley aplicable disponga imperativamente otro fuero.
              </P>
              <P>
                En caso de que el Usuario tenga la condición de consumidor, resultará aplicable el fuero que en cada caso corresponda por normativa de consumo.
              </P>
            </>
          ),
        },
      ],
    },
    en: {
      title: "Terms of Use",
      subtitle:
        "These terms govern access to and use of the Nexum website and services. Please read them carefully; by using the service you agree to be bound by them.",
      lastUpdated: "March 2026",
      sections: [
        {
          id: "objeto",
          heading: "Purpose",
          body: (
            <>
              <P>
                These Terms of Use govern access, browsing and use of this website (<Strong>nexumbusiness.com</Strong>) and the digital services provided by <Strong>Nexum Business S.L.</Strong> (hereinafter <Strong>&ldquo;Nexum&rdquo;</Strong>, <Strong>&ldquo;we&rdquo;</Strong> or <Strong>&ldquo;the Provider&rdquo;</Strong>).
              </P>
              <P>
                Using the website makes you a User and entails the full and unreserved acceptance of all the clauses contained herein.
              </P>
            </>
          ),
        },
        {
          id: "prestador",
          heading: "Provider identification",
          body: (
            <UL>
              <LI><Strong>Legal name:</Strong> Nexum Business S.L.</LI>
              <LI><Strong>Registered office:</Strong> Barcelona, Spain</LI>
              <LI><Strong>Email:</Strong> hello@nexumbusiness.com</LI>
              <LI><Strong>Activity:</Strong> development and operation of conversational AI solutions for the automotive industry.</LI>
            </UL>
          ),
        },
        {
          id: "acceso",
          heading: "Access and use",
          body: (
            <>
              <P>
                Access to the site is <Strong>free of charge</Strong>, except for the cost of the User's own Internet connection. Certain services may require prior registration and specific agreements.
              </P>
              <P>The User undertakes to use the site in accordance with the law, morality, public order and these Terms. In particular:</P>
              <UL>
                <LI>Not to perform acts against third-party rights or the proper functioning of the service.</LI>
                <LI>Not to introduce or disseminate viruses, macros, applets, ActiveX controls or any harmful code.</LI>
                <LI>Not to tamper with or attempt to access restricted areas of the system.</LI>
                <LI>Not to use the service for unauthorized mass communications (spam).</LI>
                <LI>Not to reverse-engineer, decompile or disassemble the software.</LI>
              </UL>
            </>
          ),
        },
        {
          id: "propiedad",
          heading: "Intellectual and industrial property",
          body: (
            <>
              <P>
                All contents of the site — including but not limited to texts, photographs, graphics, images, icons, technology, software, source code, graphic design, trade names, trademarks or distinctive signs — are the <Strong>property of Nexum</Strong> or of third parties who have authorized their use.
              </P>
              <P>
                Their reproduction, distribution, public communication, transformation or any other activity involving the contents, even citing sources, is expressly prohibited without Nexum's written consent.
              </P>
              <Callout>
                Any unauthorized use will be deemed a serious breach and may give rise to any applicable legal actions.
              </Callout>
            </>
          ),
        },
        {
          id: "enlaces",
          heading: "Links",
          body: (
            <>
              <P>
                The site may contain links to third-party websites. Nexum <Strong>does not control nor assume responsibility</Strong> for their content, privacy policies or practices.
              </P>
              <P>
                Links are provided for informational purposes only and their inclusion implies no association, merger or participation with the linked entities.
              </P>
            </>
          ),
        },
        {
          id: "responsabilidad",
          heading: "Disclaimer of warranties and liability",
          body: (
            <>
              <P>
                Nexum makes its best efforts to keep the site and service operational, secure and error-free. Nevertheless, it cannot guarantee the <Strong>absolute absence</Strong> of interruptions or errors in access, or the absence of viruses or other harmful elements introduced by third parties outside Nexum.
              </P>
              <P>Nexum is released from any liability arising from:</P>
              <UL>
                <LI>Occasional unavailability of the site due to force majeure or maintenance.</LI>
                <LI>Improper use of the site by Users.</LI>
                <LI>Contents and opinions expressed by third parties through the site's channels.</LI>
                <LI>Damages arising from the use or interpretation of published information.</LI>
              </UL>
            </>
          ),
        },
        {
          id: "modificaciones",
          heading: "Modifications",
          body: (
            <P>
              Nexum reserves the right to make <Strong>changes</Strong> at any time and without notice to the information contained in the site, to its configuration and presentation, or to the access conditions. We recommend reviewing this document periodically.
            </P>
          ),
        },
        {
          id: "duracion",
          heading: "Duration and termination",
          body: (
            <P>
              The site and its services have, in principle, an <Strong>indefinite duration</Strong>. Nexum may unilaterally terminate, suspend or interrupt the service and the website without notice, without prejudice to specific contractual conditions.
            </P>
          ),
        },
        {
          id: "legislacion",
          heading: "Applicable law and jurisdiction",
          body: (
            <>
              <P>
                The relationship between Nexum and the User is governed by <Strong>Spanish law</Strong> in force. Any dispute will be submitted to the Courts of the city of <Strong>Barcelona</Strong>, with express waiver of any other jurisdiction that may correspond, unless applicable law mandatorily provides otherwise.
              </P>
              <P>
                Where the User qualifies as a consumer, the jurisdiction determined by consumer regulations will apply.
              </P>
            </>
          ),
        },
      ],
    },
  };

  const c = content[lang];
  return (
    <LegalLayout
      lang={lang}
      setLang={setLang}
      meta={{ title: c.title, subtitle: c.subtitle, lastUpdated: c.lastUpdated }}
      sections={c.sections}
      related={[
        { href: "/privacidad", label: lang === "es" ? "Política de Privacidad" : "Privacy Policy" },
        { href: "/cookies", label: lang === "es" ? "Política de Cookies" : "Cookie Policy" },
        { href: "/legal", label: lang === "es" ? "Aviso Legal" : "Legal Notice" },
      ]}
    />
  );
}
